import { keywordsMeta } from "@/lib/seo/keywords";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { TrustBand } from "@/components/site/TrustBand";
import { getAudience, audiences } from "@/content/audiences";
import { AudienceHero } from "@/components/site/audience/AudienceHero";
import { AudienceProcessSteps } from "@/components/site/audience/AudienceProcessSteps";
import { AudienceProofRow } from "@/components/site/audience/AudienceProofRow";
import { AudienceLeadMagnet } from "@/components/site/audience/AudienceLeadMagnet";
import { AudienceFAQ } from "@/components/site/audience/AudienceFAQ";

import { AudienceClosing } from "@/components/site/audience/AudienceClosing";
import { Check, ArrowRight } from "lucide-react";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { StillsMarquee } from "@/components/site/StillsMarquee";
import { aeoGeo } from "@/lib/seo/schema";

const validSlugs = audiences.map((a) => a.slug) as string[];

export const Route = createFileRoute("/for-vem_/$slug")({
  beforeLoad: ({ params }) => {
    if (!validSlugs.includes(params.slug)) throw notFound();
  },
  loader: ({ params }) => {
    const audience = getAudience(params.slug)!;
    return { audience };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.audience;
    if (!a) return { meta: [{ title: "Fenny" }] };
    const title = `Filmproduktion för ${a.label.toLowerCase()} | Fenny`;
    const description = a.longIntro.slice(0, 165);
    return {
      meta: [
        ...keywordsMeta(a.to.replace("https://fenny.se", "")),
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: a.to },
      ],
      links: [{ rel: "canonical", href: a.to }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: a.to.replace("https://fenny.se", "") })) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: a.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Fenny — film för ${a.label.toLowerCase()}`,
            provider: { "@type": "Organization", name: "Fenny" },
            audience: { "@type": "Audience", audienceType: a.label },
            description: a.longIntro,
            serviceType: ["Videoproduktion"],
          }),
        },
      ],
    };
  },
  component: AudiencePage,
});

function AudiencePage() {
  const { audience: a } = Route.useLoaderData();
  const others = audiences.filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <div>
      <AudienceHero audience={a} />


      <Section className="pt-12 md:pt-20">
        <div className="grid gap-4 md:grid-cols-2 md:gap-6">
          <article className="rounded-3xl border border-white/10 bg-surface p-6 md:p-10">
            <div className="text-xs uppercase tracking-widest text-mint">
              Det här känner ni igen
            </div>
            <h2 className="mt-3 text-xl font-semibold md:text-3xl">Vad som klämmer</h2>
            <ul className="mt-4 space-y-3 text-sm md:mt-6">
              {a.pains.map((p: string) => (
                <li key={p} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </article>
          <article className="rounded-3xl border border-white/10 bg-surface p-6 md:p-10">
            <div className="text-xs uppercase tracking-widest text-peach">
              Så ser det ut med oss
            </div>
            <h2 className="mt-3 text-xl font-semibold md:text-3xl">Vad ni får</h2>
            <ul className="mt-4 space-y-3 text-sm md:mt-6">
              {a.outcomes.map((o: string) => (
                <li key={o} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-peach" strokeWidth={2.5} />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </Section>


      <AudienceProcessSteps audience={a} />

      <AudienceProofRow audience={a} />

      <StillsMarquee
        images={a.stills}
        accent="peach"
        eyebrow={`Bakom kulisserna · ${a.label}`}
        title="Så ser produktionerna ut."
        lede="Stillbilder från set, ljus och människorna framför linsen i färska Fenny-produktioner."
      />


      <Section className="pt-0">
        <figure className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-surface/60 p-8 md:p-12">
          <blockquote className="text-balance text-2xl font-medium leading-snug md:text-3xl">
            "{a.quote.text}"
          </blockquote>
          <figcaption className="mt-4 text-sm text-muted-foreground">
            — {a.quote.who}
          </figcaption>
        </figure>
      </Section>


      <AudienceLeadMagnet audience={a} />

      <AudienceFAQ audience={a} />

      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Andra spår"
          title="Är ni snarare något av dessa?"
          align="center"
        />
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {others.map((o) => (
            <Link
              key={o.slug}
              to="/for-vem/$slug"
              params={{ slug: o.slug }}
              className="group flex flex-col rounded-2xl border border-white/10 bg-surface p-6 transition-colors hover:border-white/20"
            >
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {o.label}
              </div>
              <div className="mt-2 text-base font-semibold">{o.intro}</div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-foreground/80 group-hover:text-foreground">
                Läs mer <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <AudienceClosing audience={a} />

      <SignatureCta
        eyebrow={`Spår · ${a.label}`}
        title="Boka 30 minuter. Vi designar er rytm."
        body={`Kostnadsfritt strategimöte med fokus på just ${a.label.toLowerCase()}. Vi pratar mål, kanaler och vad ni faktiskt behöver leverera.`}
        tone="peach"
      />

      <TrustBand ctaLocation={`audience_${a.slug}_trust_band`} />
    </div>
  );
}
